import React, { useEffect, useState } from 'react'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../../common/SafeIcon'
import { fetchLessonTemplates } from '../../../config/lessonService'
import { supabaseConfigError } from '../../../config/supabase'

const { FiLayers, FiRefreshCw, FiAlertTriangle, FiSearch } = FiIcons

const CurriculumTemplatesPage = () => {
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [subjectFilter, setSubjectFilter] = useState('')

  const loadTemplates = async () => {
    if (supabaseConfigError) {
      setError(supabaseConfigError.message)
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const { data, error: fetchError } = await fetchLessonTemplates(subjectFilter.trim())
      if (fetchError) throw fetchError
      setTemplates(data || [])
    } catch (fetchError) {
      console.error('Error loading templates', fetchError)
      setError('Unable to load templates right now. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTemplates()
  }, [])

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm uppercase tracking-wider text-gray-500">Curriculum Builder</p>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <SafeIcon icon={FiLayers} className="w-8 h-8 text-[#00AAFF]" />
            Templates
          </h1>
          <p className="text-gray-600 mt-2">
            Reuse lessons across curricula with a growing library of templates for parents and teachers.
          </p>
        </div>
        <button
          onClick={loadTemplates}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-gray-200 text-gray-700 hover:border-[#00AAFF] hover:text-[#00AAFF] transition-colors"
        >
          <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-xl shadow p-4 sm:p-6 border border-gray-100">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 w-full sm:max-w-md">
            <div className="relative flex-1">
              <FiSearch className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                value={subjectFilter}
                onChange={(e) => setSubjectFilter(e.target.value)}
                placeholder="Filter by subject"
                className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
              />
            </div>
            <button
              onClick={loadTemplates}
              className="inline-flex items-center px-4 py-2 bg-[#00AAFF] text-white rounded-lg hover:bg-[#0099E5] transition-colors"
            >
              Apply
            </button>
          </div>
          <p className="text-sm text-gray-500">
            Templates can be applied while authoring lessons.
          </p>
        </div>

        {error && (
          <div className="mt-4 flex items-center gap-2 text-red-600 bg-red-50 border border-red-100 rounded-lg p-3">
            <SafeIcon icon={FiAlertTriangle} className="w-5 h-5" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading
            ? Array.from({ length: 6 }).map((_, idx) => (
                <div key={idx} className="animate-pulse h-40 bg-gray-100 rounded-lg" />
              ))
            : templates.length > 0
              ? templates.map((template) => (
                  <div
                    key={template.id}
                    className="p-4 border border-gray-200 rounded-lg hover:border-[#00AAFF] transition-colors bg-white"
                  >
                    <p className="text-xs uppercase tracking-wider text-gray-500">Template</p>
                    <h3 className="text-lg font-semibold text-gray-900 mt-1">{template.title}</h3>
                    <p className="text-sm text-gray-600 line-clamp-3 mt-2">{template.description}</p>
                    <div className="flex items-center justify-between text-sm text-gray-500 mt-3">
                      <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                        {template.subject || 'General'}
                      </span>
                      <span>{new Date(template.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))
              : (
                <div className="col-span-full text-center py-10 text-gray-600 border border-dashed border-gray-200 rounded-lg">
                  <p className="font-semibold mb-1">No templates found</p>
                  <p className="text-sm text-gray-500">Try adjusting your subject filter or create a new template while authoring a lesson.</p>
                </div>
              )}
        </div>
      </div>
    </div>
  )
}

export default CurriculumTemplatesPage
